import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card } from '../ui/card';
import { essayPrompts } from '../../mockData';
import { FileText } from 'lucide-react';

const EssaysForm = ({ data, onSave }) => {
  const [essayData, setEssayData] = useState({
    common_app_essay: data?.common_app_essay || {
      prompt: essayPrompts[0],
      content: ''
    },
    supplemental_essays: data?.supplemental_essays || {}
  });

  const handlePromptChange = (value) => {
    setEssayData({
      ...essayData,
      common_app_essay: {
        ...essayData.common_app_essay,
        prompt: value
      }
    });
  };

  const handleContentChange = (value) => {
    setEssayData({
      ...essayData,
      common_app_essay: {
        ...essayData.common_app_essay,
        content: value
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(essayData);
  };

  const wordCount = essayData.common_app_essay?.content?.trim().split(/\s+/).filter(w => w.length > 0).length || 0;
  const maxWords = 650;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>College Essay Requirements:</strong>
          <br />• Choose one of the prompts below
          <br />• Write a personal essay of 250-650 words
          <br />• Be authentic and reflective
          <br />• Proofread carefully
        </p>
      </div>

      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="essayPrompt">Select Essay Prompt *</Label>
            <Select
              value={essayData.common_app_essay?.prompt}
              onValueChange={handlePromptChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {essayPrompts.map((prompt, index) => (
                  <SelectItem key={index} value={prompt}>
                    Prompt {index + 1}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg border">
            <p className="text-sm text-gray-700 leading-relaxed">
              {essayData.common_app_essay?.prompt}
            </p>
          </div>

          <div>
            <Label htmlFor="essayContent">Your Essay *</Label>
            <Textarea
              id="essayContent"
              value={essayData.common_app_essay?.content}
              onChange={(e) => handleContentChange(e.target.value)}
              placeholder="Begin writing your essay here..."
              rows={20}
              className="font-serif"
              required
            />
            <div className="flex justify-between items-center mt-2">
              <p className="text-sm text-gray-500">
                Word count: <span className={wordCount > maxWords ? 'text-red-600 font-semibold' : 'font-semibold'}>{wordCount}</span> / {maxWords}
              </p>
              {wordCount > maxWords && (
                <p className="text-sm text-red-600">⚠️ Exceeds maximum word count</p>
              )}
              {wordCount < 250 && wordCount > 0 && (
                <p className="text-sm text-yellow-600">⚠️ Below minimum word count (250)</p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Supplemental Essays Info */}
      <Card className="p-6 bg-gray-50">
        <div className="flex items-start space-x-3">
          <FileText className="h-5 w-5 text-gray-600 mt-1" />
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Supplemental Essays</h3>
            <p className="text-sm text-gray-600">
              Some colleges require additional essays specific to their institution. These will be added after you select your colleges and vary by school.
            </p>
          </div>
        </div>
      </Card>

      <div className="flex justify-end space-x-3">
        <Button type="button" variant="outline" onClick={() => {
          const saved = localStorage.getItem('essayDraft');
          if (saved) {
            const draft = JSON.parse(saved);
            setEssayData(draft);
          }
        }}>
          Load Draft
        </Button>
        <Button type="button" variant="outline" onClick={() => {
          localStorage.setItem('essayDraft', JSON.stringify(essayData));
        }}>
          Save Draft
        </Button>
        <Button type="submit">Save & Continue</Button>
      </div>
    </form>
  );
};

export default EssaysForm;